import React from 'react';
import { getContext } from 'common';
import { useSpring, animated } from 'react-spring';
import bezierEasing from 'bezier-easing';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import _merge from 'lodash/merge';
import Styled from './styles';
import { LaunchPadContext } from './LaunchPad';

export interface LaunchPadHeaderProps extends React.ComponentPropsWithRef<'div'> {
  /** Place content here. */
  children: React.ReactNode;
  /** Control animation config here, used in conjunction with `animateIn` prop of Launchpad. This uses React-spring config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** @ignore */
  style?: React.CSSProperties
}

/** Place header content in here. This component is optional and only useful when trying to animate header content excluding the Logo. */
const LaunchPadHeader = React.forwardRef<HTMLElement, LaunchPadHeaderProps>((props: LaunchPadHeaderProps, ref) =>
  getContext(({ RTL }) => {
    const {
      children, style, animationProps, ...otherProps
    } = props;
    const { animateIn, disableAnimation } = React.useContext(LaunchPadContext);
    const springProps = animateIn
      ? useSpring(animationProps || {
        to: { opacity: 1 },
        from: { opacity: 0 },
        delay: 1000,
        config: {
          duration: parseInt(SPEED_SLOW, 10) * 3,
          easing: bezierEasing(0.4, 0, 0.2, 1) // TRANSITION_STANDARD
        },
        immediate: disableAnimation,
      })
      : {};
    const resolvedStyles = _merge({}, springProps, style);
    return (
      <Styled.LaunchPadHeaderInner $rtl={RTL} ref={ref} as={animated.div} style={resolvedStyles} {...otherProps}>
        {children}
      </Styled.LaunchPadHeaderInner>
    );
  }));

LaunchPadHeader.displayName = 'LaunchPadHeader';
LaunchPadHeader.defaultProps = {
  animationProps: undefined,
  style: {}
};

/* Exports */
export { LaunchPadHeader as default };
